// frontend/src/pages/builder/matrixSlots.js
// Phase 3 step 7e-3 (decision D12): the Matrix visual's three slots -- Rows,
// Columns, Values -- and the spec it sends. Pure and unit-tested; no React.
//
// The draft stays { dimensions, measures }: the row fields come first, the
// column fields after them, and `mx` = { columns, totals } (kept beside the
// draft) says how many of the trailing dimensions are column fields. The
// layout sent to the server (spec.matrix, 7e-1) is worked out from both on
// every render, so the draft and the layout can never disagree.
import {
  MAX_MEASURES, defaultUse, dimensionItem, dimensionLabel, dropField, isMeasureField, measureLabel, measureOnlyNotice,
  removeItem, sameMeasure,
} from './builderSpec';
import { selectedSortKey } from './dataSettings';

export const MAX_ROW_FIELDS = 5;        // mirrors MAX_MATRIX_ROWS in app/semantic/spec.py
export const MAX_COLUMN_FIELDS = 2;     // mirrors MAX_MATRIX_COLUMNS

export const emptyMatrix = () => ({ columns: 0, totals: true });

/** Why the draft can't be a matrix, or null. */
export function matrixReason(draft) {
  const n = draft.dimensions.length;
  if (n === 0) return 'A matrix needs a field on Rows.';
  if (n > MAX_ROW_FIELDS + MAX_COLUMN_FIELDS) {
    return `A matrix can have up to ${MAX_ROW_FIELDS} row fields and ${MAX_COLUMN_FIELDS} column fields. Remove some fields.`;
  }
  return null;
}

/**
 * The layout for the server, { rows, columns, totals }, or null when the
 * draft can't be a matrix. The stored column count is kept within what the
 * fields allow: at least one row field, at most 5 rows and 2 columns.
 */
export function matrixLayout(draft, mx) {
  if (matrixReason(draft)) return null;
  const n = draft.dimensions.length;
  const want = mx && Number.isInteger(mx.columns) ? mx.columns : 0;
  const columns = Math.min(Math.max(want, n - MAX_ROW_FIELDS, 0), MAX_COLUMN_FIELDS, n - 1);
  return { rows: n - columns, columns, totals: !(mx && mx.totals === false) };
}

/** The three slots as chips, each knowing where it lives in the draft. */
export function matrixWells(draft, mx, fieldsByKey = {}) {
  const rows = draft.dimensions.length - ((matrixLayout(draft, mx) || { columns: 0 }).columns);
  const dims = draft.dimensions.map((d, index) => ({ kind: 'dimension', index, item: d, label: dimensionLabel(d, fieldsByKey) }));
  return {
    rows: dims.slice(0, rows),
    columns: dims.slice(rows),
    values: draft.measures.map((m, index) => ({ kind: 'measure', index, item: m, label: measureLabel(m, fieldsByKey) })),
  };
}

function split(draft, mx) {
  const layout = matrixLayout(draft, mx);
  const rows = layout ? layout.rows : draft.dimensions.length;
  return { rows: draft.dimensions.slice(0, rows), cols: draft.dimensions.slice(rows) };
}

/**
 * Put a field on Rows or Columns (at the end of that slot), or on Values.
 * A field already used as a row or column field is moved, keeping its grain.
 * Returns { draft, mx, notice }; on a refusal draft and mx are unchanged.
 */
export function dropOnMatrix(draft, mx, field, well) {
  if (well !== 'values' && isMeasureField(field)) return { draft, mx, notice: measureOnlyNotice(field) };   // 13c-3
  if (well === 'values') {
    const r = dropField(draft, field, 'values');
    return { draft: r.draft, mx, notice: r.notice };
  }
  const { rows, cols } = split(draft, mx);
  const existing = draft.dimensions.find((d) => d.field === field.key);
  const item = existing || dimensionItem(field);
  const r = rows.filter((d) => d.field !== field.key);
  const c = cols.filter((d) => d.field !== field.key);
  if (well === 'rows') r.push(item); else c.push(item);
  if (r.length === 0) return { draft, mx, notice: 'A matrix needs at least one field on Rows.' };
  if (r.length > MAX_ROW_FIELDS) return { draft, mx, notice: `A matrix can have up to ${MAX_ROW_FIELDS} row fields.` };
  if (c.length > MAX_COLUMN_FIELDS) return { draft, mx, notice: `A matrix can have up to ${MAX_COLUMN_FIELDS} column fields.` };
  return { draft: { ...draft, dimensions: [...r, ...c] }, mx: { ...mx, columns: c.length }, notice: null };
}

/** Clicking a field in the list: numbers and ids become values, everything else a row field. */
export function addToMatrix(draft, mx, field) {
  const use = defaultUse(field);
  if (use.kind === 'measure') {
    if (draft.measures.some((m) => sameMeasure(m, use.item))) return { draft, mx, notice: `${field.display_name} is already in the matrix.` };
    if (draft.measures.length >= MAX_MEASURES) return { draft, mx, notice: `A matrix can show up to ${MAX_MEASURES} values.` };
    return { draft: { ...draft, measures: [...draft.measures, use.item] }, mx, notice: null };
  }
  if (draft.dimensions.some((d) => d.field === field.key)) return { draft, mx, notice: `${field.display_name} is already in the matrix.` };
  return dropOnMatrix(draft, mx, field, 'rows');
}

/** Remove a chip; removing a column field shrinks the Columns slot. */
export function removeMatrixItem(draft, mx, kind, index) {
  const { rows } = split(draft, mx);
  const next = removeItem(draft, kind, index);
  if (kind !== 'dimension') return { draft: next, mx };
  const cols = draft.dimensions.length - rows.length;
  return { draft: next, mx: { ...mx, columns: index >= rows.length ? cols - 1 : cols } };
}

/**
 * Drag a chip from one slot to another. A grouping moved to Values becomes a
 * value (distinct count); a value moved to Rows or Columns becomes a
 * grouping. Nothing changes when the move isn't allowed; the notice says why.
 */
export function moveMatrixChip(draft, mx, kind, index, field, well) {
  if (!field) return { draft, mx, notice: null };
  if (kind === 'measure' && well === 'values') return { draft, mx, notice: null };
  if (kind === 'dimension' && well !== 'values') return dropOnMatrix(draft, mx, field, well);
  const removed = removeMatrixItem(draft, mx, kind, index);
  if (kind === 'dimension' && removed.draft.dimensions.length === 0) {
    return { draft, mx, notice: 'A matrix needs at least one field on Rows.' };
  }
  const r = dropOnMatrix(removed.draft, removed.mx, field, well);
  return r.notice ? { draft, mx, notice: r.notice } : r;
}

/**
 * The spec sent for a matrix: the fields, the layout, and a sort only when
 * the user chose one (automatic = the hierarchy A to Z, which the server
 * does without a sort). Empty periods are a chart setting and not sent.
 */
export function matrixSpec(fieldSpec, settings, layout) {
  if (!fieldSpec || !layout) return fieldSpec;
  const out = { dataset_id: fieldSpec.dataset_id, dimensions: fieldSpec.dimensions, measures: fieldSpec.measures, matrix: layout };
  if (fieldSpec.filters && fieldSpec.filters.length) out.filters = fieldSpec.filters;   // step 8
  const chosen = settings && settings.sort;
  if (chosen && selectedSortKey(fieldSpec, settings) !== 'auto') out.sort = [{ ...chosen.target, dir: chosen.dir }];
  if (settings && settings.limit) out.limit = settings.limit;
  return out;
}
